import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import {
  getClient,
  listClientProjects,
  requestUpgrade,
  assignProjectsToClient,
  listOwnProjects,
  PartnerClientDetail as ClientDetailType,
  ProjectWithLatest,
} from "../../api/partner";

export default function PartnerClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const {
    data: client,
    isLoading: clientLoading,
    error: clientError,
  } = useQuery<ClientDetailType>({
    queryKey: ["partner-client", clientId],
    queryFn: () => getClient(clientId!, token!),
    enabled: !!token && !!clientId,
  });

  const {
    data: projects = [],
    isLoading: projectsLoading,
  } = useQuery<ProjectWithLatest[]>({
    queryKey: ["partner-client-projects", clientId],
    queryFn: () => listClientProjects(clientId!, token!),
    enabled: !!token && !!clientId,
  });

  // Partner's own unassigned projects (for the assign modal)
  const { data: ownProjects = [], isLoading: ownProjectsLoading } = useQuery<ProjectWithLatest[]>({
    queryKey: ["partner-own-projects"],
    queryFn: () => listOwnProjects(token!),
    enabled: !!token && showAssign,
  });

  const upgradeMutation = useMutation({
    mutationFn: () => requestUpgrade(clientId!, { message: upgradeMessage }, token!),
    onSuccess: () => {
      setUpgradeSuccess(true);
    },
  });

  const assignMutation = useMutation({
    mutationFn: (projectIds: string[]) =>
      assignProjectsToClient(clientId!, { project_ids: projectIds }, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["partner-client-projects", clientId] });
      queryClient.invalidateQueries({ queryKey: ["partner-clients"] });
      queryClient.invalidateQueries({ queryKey: ["partner-own-projects"] });
      setShowAssign(false);
      setSelectedIds(new Set());
    },
  });

  if (clientLoading) {
    return (
      <div className="px-6 py-8 text-text-secondary">Loading client...</div>
    );
  }

  if (clientError || !client) {
    return (
      <div className="px-6 py-8">
        <div className="bg-red-50/10 border border-health-400/30 text-health-400 px-4 py-3 rounded">
          {clientError ? (clientError as Error).message : "Client not found"}
        </div>
      </div>
    );
  }

  const statusColor = (status: string | null) => {
    switch (status) {
      case "completed":
        return "text-quantum-400";
      case "running":
      case "queued":
        return "text-yellow-400";
      case "failed":
        return "text-health-400";
      default:
        return "text-text-muted";
    }
  };

  const toggleProject = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="px-6 pb-6">
      <Link
        to="/app/clients"
        className="text-sm text-quantum-400 hover:underline"
      >
        &larr; Back to Clients
      </Link>

      {/* Client header */}
      <div className="mt-4 mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            {client.client_name}
          </h1>
          {client.contact_email && (
            <p className="text-body-sm text-text-muted mt-1">
              {client.contact_email}
            </p>
          )}
          <p className="text-body-sm text-text-secondary mt-1">
            {client.project_count} project{client.project_count !== 1 ? "s" : ""}
            {" / "}
            {client.portfolios.length} portfolio{client.portfolios.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowAssign(true)}
            className="btn-secondary"
          >
            Assign Projects
          </button>
          <button
            onClick={() => setShowUpgrade(true)}
            className="btn-secondary"
          >
            Request Upgrade
          </button>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgrade && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => { setShowUpgrade(false); setUpgradeSuccess(false); }}
        >
          <div
            className="w-full max-w-md rounded-xl bg-surface-700 border border-border p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {upgradeSuccess ? (
              <>
                <h3 className="mb-2 text-h4 text-quantum-400">Request Submitted</h3>
                <p className="text-body-sm text-text-secondary mb-4">
                  Our sales team will contact you about upgrading{" "}
                  <strong>{client.client_name}</strong> to a full TotalView customer.
                </p>
                <div className="flex justify-end">
                  <button
                    className="btn-primary"
                    onClick={() => { setShowUpgrade(false); setUpgradeSuccess(false); }}
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="mb-2 text-h4 text-text-primary">
                  Request Customer Upgrade
                </h3>
                <p className="text-body-sm text-text-secondary mb-4">
                  Submit a request to upgrade <strong>{client.client_name}</strong> to
                  a full TotalView customer with their own login and full results access.
                </p>

                <label className="block text-body-sm text-text-secondary mb-1">
                  Message (optional)
                </label>
                <textarea
                  value={upgradeMessage}
                  onChange={(e) => setUpgradeMessage(e.target.value)}
                  className="w-full rounded-lg bg-surface-600 border border-border px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500 h-24 resize-none"
                  placeholder="Any context for the sales team..."
                />

                <div className="mt-4 flex justify-end gap-3">
                  <button
                    className="btn-secondary"
                    onClick={() => setShowUpgrade(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => upgradeMutation.mutate()}
                    disabled={upgradeMutation.isPending}
                  >
                    {upgradeMutation.isPending ? "Submitting..." : "Submit Request"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Assign Projects Modal */}
      {showAssign && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => { setShowAssign(false); setSelectedIds(new Set()); }}
        >
          <div
            className="w-full max-w-lg rounded-xl bg-surface-700 border border-border p-6 shadow-xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-1 text-h4 text-text-primary">
              Assign Projects to {client.client_name}
            </h3>
            <p className="text-body-sm text-text-secondary mb-4">
              Select projects from your organization to move to this client.
            </p>

            {ownProjectsLoading ? (
              <p className="text-text-secondary text-body-sm py-4">Loading projects...</p>
            ) : ownProjects.length === 0 ? (
              <p className="text-text-secondary text-body-sm py-4">
                No unassigned projects in your organization.
              </p>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-1 mb-4 -mx-1 px-1">
                {ownProjects.map((proj) => (
                  <label
                    key={proj.project_id}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                      selectedIds.has(proj.project_id)
                        ? "bg-quantum-500/10 border border-quantum-500/30"
                        : "bg-surface-800 border border-border hover:border-quantum-500/30"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(proj.project_id)}
                      onChange={() => toggleProject(proj.project_id)}
                      className="rounded border-border text-quantum-500 focus:ring-quantum-500"
                    />
                    <span className="font-medium text-text-primary text-sm">
                      {proj.project_name}
                    </span>
                    <span className={`ml-auto text-xs ${statusColor(proj.latest_run_status)}`}>
                      {proj.latest_run_status || "No runs"}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {assignMutation.isError && (
              <p className="text-health-400 text-body-sm mb-2">
                {(assignMutation.error as Error).message}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-border">
              <button
                className="btn-secondary"
                onClick={() => { setShowAssign(false); setSelectedIds(new Set()); }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => assignMutation.mutate(Array.from(selectedIds))}
                disabled={selectedIds.size === 0 || assignMutation.isPending}
              >
                {assignMutation.isPending
                  ? "Moving..."
                  : `Assign ${selectedIds.size} Project${selectedIds.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolios */}
      {client.portfolios.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-medium text-text-primary mb-3">Portfolios</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {client.portfolios.map((p) => (
              <div
                key={p.portfolio_id}
                className="rounded-lg bg-surface-800 border border-border p-4"
              >
                <h4 className="font-medium text-text-primary">{p.portfolio_name}</h4>
                {p.description && (
                  <p className="text-body-sm text-text-muted mt-1">{p.description}</p>
                )}
                <p className="text-body-sm text-text-secondary mt-2">
                  {p.project_count} project{p.project_count !== 1 ? "s" : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-text-primary">Projects</h2>
          <button
            onClick={() => navigate(`/app/projects?client=${clientId}`)}
            className="text-sm text-quantum-400 hover:underline"
          >
            + Create Project
          </button>
        </div>

        {projectsLoading && (
          <p className="text-text-secondary text-body-sm">Loading projects...</p>
        )}

        {!projectsLoading && projects.length === 0 && (
          <div className="text-center py-8 text-text-secondary rounded-lg border border-border bg-surface-800">
            <p className="mb-2">No projects yet</p>
            <p className="text-body-sm">Create a project or assign existing projects from your organization.</p>
          </div>
        )}

        {projects.length > 0 && (
          <div className="space-y-2">
            {projects.map((proj) => (
              <div
                key={proj.project_id}
                onClick={() =>
                  navigate(
                    `/app/projects/${encodeURIComponent(proj.project_id)}?name=${encodeURIComponent(proj.project_name)}`
                  )
                }
                className="cursor-pointer flex items-center justify-between rounded-lg bg-surface-800 border border-border px-4 py-3 hover:border-quantum-500/50 transition-colors"
              >
                <span className="font-medium text-text-primary">
                  {proj.project_name}
                </span>
                <span className={`text-body-sm ${statusColor(proj.latest_run_status)}`}>
                  {proj.latest_run_status || "No runs"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
